<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	//Design system
	import { Alert, Button, Modal } from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	//Components
	import BleWebInclusionG2 from './BleWebInclusionG2.svelte';
	import {
		checkBLUDeviceAccount,
		checkDeviceAccount,
		preparingBLUDeviceForInclusion,
		preparingDeviceForInclusion,
	} from 'src/helpers/web_bluetooth/web_bluetooth_inclusion_helpers';
	import WebBluetoothEvents from 'src/helpers/web_bluetooth/web_bluetooth_events';
	import WebBluetoothService from 'src/helpers/web_bluetooth/web_bluetooth_service';
	import WebBluetoothClient from 'src/helpers/web_bluetooth/web_bluetooth_client';
	import ExitConfirmationInclusionModal from './ExitConfirmationInclusionModal.svelte';
	import BleWebInclusionDeviceExist from './BleWebInclusionDeviceExist.svelte';
	import TransitionWrapper from '../Utility/TransitionWrapper.svelte';
	import BleWebInclusionScan from './BleWebInclusionScan.svelte';
	import BleWebInclusionNotSupported from './BleWebInclusionNotSupported.svelte';
	//Types
	import type { WebBLEData, WebBLEEvent } from 'src/helpers/web_bluetooth/web_bluetooth_types';
	import type { Device } from 'src/types/Device';
	//Stores
	import { navigateToDevice } from 'src/stores/ui/navigate-to';
	import { pairingMessageWrapper } from 'src/helpers/inclusion_helpers';

	export let showModal = false;

	let waitForResponse = false;
	let showConfirmation = false;
	let deviceAlreadyExist: Device = null;
	let bluDeviceAlreadyExist: Device = null;
	let bluDeviceData: WebBLEData = null;
	let inclusionStep = 'init';
	let service: WebBluetoothService;
	let client: WebBluetoothClient;
	let bleInclusionG2: BleWebInclusionG2;
	let bleRpcClientError = '';
	let connectionError = '';
	let rpcError = '';
	let filterPrefix = '';

	onMount(() => {
		service = new WebBluetoothService();
		service.addEventListener(WebBluetoothEvents.CLOSED, onCloseService);
		service.addEventListener(WebBluetoothEvents.BLE_PAIR_REQUIRED, onPairRequired);
		return () => {
			service.removeEventListener(WebBluetoothEvents.CLOSED, onCloseService);
			service.removeEventListener(WebBluetoothEvents.BLE_PAIR_REQUIRED, onPairRequired);
			disconnectClient();
		};
	});

	const closeModal = () => {
		if (!waitForResponse && inclusionStep == 'init') {
			showModal = false;
		} else {
			showConfirmation = true;
		}
	};

	const handleNext = () => {
		if (deviceAlreadyExist) {
			deviceAlreadyExist = null;
			return;
		} else if (bluDeviceAlreadyExist) {
			navigateToDevice(bluDeviceAlreadyExist.id);
			showModal = false;
			return;
		}
		waitForResponse = true;
		bleRpcClientError = '';
		connectionError = '';
		if (inclusionStep == 'init') {
			initClient();
		} else if (inclusionStep == WebBluetoothEvents.BLE_PAIR_REQUIRED) {
			client.connect();
		} else if (
			inclusionStep == WebBluetoothEvents.CONNECTED &&
			!client?.isBLUDevice &&
			bleInclusionG2
		) {
			waitForResponse = false;
			bleInclusionG2.handleNext();
		}
	};

	const initClient = async () => {
		disconnectClient();
		client = await service.startScan(filterPrefix);
		if (client) {
			client.addEventListener(WebBluetoothEvents.CONNECTED, onConnected);
			client.addEventListener(WebBluetoothEvents.ERROR, onError);
		}
	};

	const disconnectClient = () => {
		if (client) {
			client.disconnect();
			client.removeEventListener(WebBluetoothEvents.CONNECTED, onConnected);
			client.removeEventListener(WebBluetoothEvents.ERROR, onError);
		}
	};

	const onConnected = (e: WebBLEEvent) => {
		waitForResponse = false;
		bleRpcClientError = '';
		if (client.isBLUDevice) {
			bluDeviceData = e.detail.data;
			if (/SBRK-002C/i.test(e.detail.name || '')) {
				bluDeviceData = { ...bluDeviceData, type: 'SBRK-002C' };
			}
			bluDeviceAlreadyExist = checkBLUDeviceAccount(bluDeviceData);
			if (!bluDeviceAlreadyExist) {
				onFinisBLUDevice(bluDeviceData, e.detail.name);
			}
		} else {
			inclusionStep = e.type;
		}
	};
	const onPairRequired = (e: WebBLEEvent) => {
		waitForResponse = false;
		inclusionStep = e.type;
	};
	const onFinishWifiSetting = (e: CustomEvent) => {
		waitForResponse = false;
		const data = e.detail;
		const deviceG2AlreadyExist = checkDeviceAccount(data.deviceInfo);
		if (deviceG2AlreadyExist) {
			navigateToDevice(deviceG2AlreadyExist.id);
		} else {
			preparingDeviceForInclusion(data.deviceInfo, data.status, data.settings);
		}
		showModal = false;
	};
	const onFinisBLUDevice = (data: WebBLEData, bleName?: string) => {
		preparingBLUDeviceForInclusion(data, bleName);
		showModal = false;
	};
	const onError = async (e: WebBLEEvent) => {
		waitForResponse = false;
		const eventData = e.detail;
		if (eventData.error.type == WebBluetoothEvents.Errors.DATA_ERROR) {
			bleRpcClientError =
				eventData.error.message ||
				$format('error_global', {
					default: 'Something went wrong!',
				});
		} else if (
			inclusionStep == WebBluetoothEvents.BLE_PAIR_REQUIRED &&
			eventData.error.type == WebBluetoothEvents.Errors.CONNECTION_ERROR
		) {
			connectionError = $format('alert_message_ble_pair_required_error', {
				default: 'Pairing failed. Please try again.',
			});
		} else {
			if (
				/authentication\sfailed/i.test(eventData?.error?.message || '') &&
				client?.forgetDevice
			) {
				await client.forgetDevice();
				disconnectClient();
			}
			inclusionStep = 'init';
			connectionError = $format('alert_message_ble_pair_error', {
				default: 'Connection failed. Please try again.',
			});
		}
		console.log(e);
	};
	const onCloseService = (e: WebBLEEvent) => {
		waitForResponse = false;
		inclusionStep = 'init';
	};
</script>

<Modal
	on:close={closeModal}
	{showModal}
	title={capFirstChar(
		$format('add_devices', {
			default: 'add devices',
		}),
	)}
>
	<div slot="content" class="content-container">
		{#if !WebBluetoothService.isSupported}
			<BleWebInclusionNotSupported />
		{:else if bleRpcClientError}
			<TransitionWrapper outDuration={0}>
				<Alert
					type="danger"
					heading={capFirstChar(
						$format('alert_title_ble_pair_prompt', {
							default: 'Pairing required',
						}),
					)}
					message={bleRpcClientError}
				/></TransitionWrapper
			>
		{:else if bluDeviceAlreadyExist}
			<TransitionWrapper outDuration={0}>
				<BleWebInclusionDeviceExist device={bluDeviceAlreadyExist} /></TransitionWrapper
			>
		{:else if inclusionStep == 'init'}
			<TransitionWrapper outDuration={0}>
				<BleWebInclusionScan bind:filterPrefix {connectionError} />
			</TransitionWrapper>
		{:else if inclusionStep == WebBluetoothEvents.BLE_PAIR_REQUIRED}
			<TransitionWrapper outDuration={0}>
				{#if connectionError}
					<Alert
						class="mb-s"
						type="danger"
						heading={capFirstChar(
							$format('alert_title_ble_pair_prompt', {
								default: 'Pairing required',
							}),
						)}
						message={connectionError}
					/>
				{/if}
				<Alert
					heading={capFirstChar(
						$format('alert_title_ble_pair_prompt', {
							default: 'Pairing required',
						}),
					)}
					message={pairingMessageWrapper(client?.connectedDevice?.name, undefined, true)}
				/>
			</TransitionWrapper>
		{:else if inclusionStep == WebBluetoothEvents.CONNECTED && client && !client.isBLUDevice}
			<BleWebInclusionG2
				{client}
				bind:rpcError
				bind:waitForResponse
				bind:deviceAlreadyExist
				bind:this={bleInclusionG2}
				on:finish={onFinishWifiSetting}
			/>
		{/if}
	</div>
	<div slot="footer">
		<div class="d-flex justify-content-end width-full">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={closeModal}>{$format('button_cancel')}</Button
			>
			<Button
				responsiveSize={{
					sm: 'm',
				}}
				loading={waitForResponse}
				disabled={waitForResponse || !WebBluetoothService.isSupported}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				on:click={handleNext}
				>{capFirstChar(
					rpcError
						? $format('err_msg_please_try_again', {
								default: 'Please try again!',
							})
						: $format('button_next', { default: 'Next' }),
				)}</Button
			>
		</div>
	</div>
</Modal>

<ExitConfirmationInclusionModal
	bind:showModal={showConfirmation}
	on:confirm={() => {
		showConfirmation = false;
		showModal = false;
	}}
/>
